import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import Sidebar from '../../components/layout/Sidebar';
import Header from '../../components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { 
  TrendingUp, 
  DollarSign, 
  FolderKanban, 
  FileText,
  Users,
  ArrowUpRight,
  ExternalLink,
  Globe,
  UserPlus,
  CheckCircle,
  XCircle,
  Clock,
  Eye,
  Handshake,
  Palette,
  MoreHorizontal,
  AlertTriangle,
  MessageSquare,
  CalendarClock,
  ChevronRight
} from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import axios from 'axios';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AgencyDashboard = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pendingOnboarding, setPendingOnboarding] = useState([]);
  const [pendingProjects, setPendingProjects] = useState([]);
  const [pendingTickets, setPendingTickets] = useState([]);
  const [tasksAwaitingClient, setTasksAwaitingClient] = useState([]);
  const [pendingQuotes, setPendingQuotes] = useState([]);
  const [actionLoading, setActionLoading] = useState({});

  useEffect(() => {
    fetchDashboard();
    fetchPendingItems();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/agency`);
      setDashboard(response.data);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingItems = async () => {
    try {
      // Fetch onboarding requests - only truly pending ones
      const onboardingRes = await axios.get(`${API}/onboarding/`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Only show items that need action: pending (need validation) or interview_scheduled (need interview completion)
      const needsAction = onboardingRes.data.filter(r => 
        r.status === 'pending' || r.status === 'interview_scheduled'
      );
      setPendingOnboarding(needsAction.slice(0, 5));

      // Fetch projects
      const projectsRes = await axios.get(`${API}/projects/`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const pendingProj = projectsRes.data.filter(p => 
        p.status === 'draft' || p.status === 'pending_approval' || p.status === 'brief_pending'
      );
      setPendingProjects(pendingProj.slice(0, 5));

      // Fetch tickets
      const ticketsRes = await axios.get(`${API}/tickets/`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const openTickets = ticketsRes.data.filter(t => t.status === 'open' || t.status === 'pending');
      setPendingTickets(openTickets.slice(0, 5));

      // Fetch tasks awaiting client validation
      const tasksRes = await axios.get(`${API}/tasks/awaiting-client`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTasksAwaitingClient(tasksRes.data || []);

      // Fetch pending quote requests
      try {
        const quotesRes = await axios.get(`${API}/website/quote-requests?status=new`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setPendingQuotes(quotesRes.data || []);
      } catch (e) {
        console.log('Quote requests endpoint not available');
      }
    } catch (error) {
      console.error('Error fetching pending items:', error);
    }
  };

  // Quick action handlers
  const handleValidateOnboarding = async (id) => {
    setActionLoading(prev => ({ ...prev, [id]: true }));
    try {
      await axios.put(`${API}/onboarding/${id}/validate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Candidature validée');
      fetchPendingItems();
    } catch (error) {
      toast.error('Erreur lors de la validation');
    } finally {
      setActionLoading(prev => ({ ...prev, [id]: false }));
    }
  };

  const handleRejectOnboarding = async (id) => {
    if (!window.confirm('Rejeter cette candidature ?')) return;
    setActionLoading(prev => ({ ...prev, [id]: true }));
    try {
      await axios.put(`${API}/onboarding/${id}/reject`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Candidature rejetée');
      fetchPendingItems();
    } catch (error) {
      toast.error('Erreur lors du rejet');
    } finally {
      setActionLoading(prev => ({ ...prev, [id]: false }));
    }
  };

  const handleCompleteInterview = async (id, result) => {
    setActionLoading(prev => ({ ...prev, [id]: true }));
    try {
      await axios.put(`${API}/onboarding/${id}/complete-interview`, { result }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(result === 'accepted' ? 'Candidature acceptée' : 'Candidature refusée');
      fetchPendingItems();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setActionLoading(prev => ({ ...prev, [id]: false }));
    }
  };

  const handleResolveTicket = async (id) => {
    setActionLoading(prev => ({ ...prev, [id]: true }));
    try {
      await axios.put(`${API}/tickets/${id}/status`, { status: 'resolved' }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Ticket résolu');
      fetchPendingItems();
    } catch (error) {
      toast.error('Erreur lors de la résolution');
    } finally {
      setActionLoading(prev => ({ ...prev, [id]: false }));
    }
  };

  const COLORS = ['#00d4aa', '#1a365d', '#f59e0b', '#6366f1', '#ec4899'];

  const projectTypeLabels = {
    logo: 'Logos',
    visual: 'Visuels',
    video: 'Vidéos',
    website: 'Sites web',
    mobile_app: 'Apps mobile'
  };

  const pieData = dashboard?.revenue_by_type 
    ? Object.entries(dashboard.revenue_by_type).map(([key, value]) => ({
        name: projectTypeLabels[key] || key,
        value
      }))
    : [];

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <Sidebar 
        collapsed={collapsed} 
        setCollapsed={setCollapsed}
        mobileOpen={mobileOpen}
        setMobileOpen={setMobileOpen}
      />
      <Header collapsed={collapsed} setMobileOpen={setMobileOpen} />
      
      <main className={cn(
        "transition-all duration-300 p-6 md:p-8 pt-24",
        collapsed ? "md:ml-20" : "md:ml-64"
      )}>
        {/* Header with Quick Actions */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-[#1a365d]" data-testid="dashboard-title">
              Bonjour, {user?.first_name} 👋
            </h1>
            <p className="text-[#64748b] mt-1">
              Voici le résumé de votre activité
            </p>
          </div>
          
          {/* Quick Action Buttons */}
          <div className="flex flex-wrap gap-3">
            <Button 
              variant="outline" 
              onClick={() => window.open('/site', '_blank')}
              className="gap-2"
            >
              <Globe className="w-4 h-4" />
              Voir le site
            </Button>
            <Button 
              onClick={() => navigate('/annuaire-creatifs')}
              className="gap-2 bg-gradient-to-r from-cyan-500 to-blue-600"
            >
              <Users className="w-4 h-4" />
              Annuaire créatifs
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="spinner" />
          </div>
        ) : (
          <>
            {/* Pending Onboarding Requests Alert */}
            {pendingOnboarding.length > 0 && (
              <Card className="mb-4 border-amber-200 bg-amber-50/50">
                <CardHeader className="py-2 px-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <UserPlus className="w-4 h-4 text-amber-600" />
                      <CardTitle className="text-sm text-slate-900">
                        Candidatures ({pendingOnboarding.length})
                      </CardTitle>
                    </div>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => navigate('/onboarding-management')}
                      className="h-7 text-xs gap-1"
                    >
                      Voir tout <ArrowUpRight className="w-3 h-3" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="pt-0 px-4 pb-3">
                  <div className="divide-y divide-amber-100">
                    {pendingOnboarding.map((request) => (
                      <div 
                        key={request.id}
                        className="flex items-center justify-between py-2 hover:bg-amber-100/50 -mx-2 px-2 rounded cursor-pointer"
                        onClick={() => navigate(`/onboarding-management?id=${request.id}`)}
                      >
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                          {request.type === 'creative' ? (
                            <Palette className="w-4 h-4 text-cyan-600 shrink-0" />
                          ) : (
                            <Handshake className="w-4 h-4 text-orange-600 shrink-0" />
                          )}
                          <span className="text-sm font-medium text-slate-800 truncate">
                            {request.questionnaire?.first_name || request.user_details?.first_name} {request.questionnaire?.last_name || request.user_details?.last_name}
                          </span>
                          <Badge variant="outline" className="text-[10px] h-5 shrink-0">
                            {request.type === 'creative' ? 'Créatif' : 'Affilié'}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          {request.status === 'pending' && (
                            <>
                              <Button
                                size="icon"
                                variant="ghost"
                                onClick={(e) => { e.stopPropagation(); handleRejectOnboarding(request.id); }}
                                disabled={actionLoading[request.id]}
                                className="h-7 w-7 text-red-600 hover:bg-red-50"
                              >
                                <XCircle className="w-4 h-4" />
                              </Button>
                              <Button
                                size="icon"
                                onClick={(e) => { e.stopPropagation(); handleValidateOnboarding(request.id); }}
                                disabled={actionLoading[request.id]}
                                className="h-7 w-7 bg-green-600 hover:bg-green-700 text-white"
                              >
                                <CheckCircle className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                          {request.status === 'interview_scheduled' && (
                            <Badge className="text-[10px] h-5 bg-purple-100 text-purple-700">Entretien</Badge>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Compact Action Sections Row */}
            {(pendingProjects.length > 0 || pendingTickets.length > 0 || tasksAwaitingClient.length > 0 || pendingQuotes.length > 0) && (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
                {/* Pending Projects */}
                {pendingProjects.length > 0 && (
                  <Card className="border-blue-200 bg-blue-50/50">
                    <CardHeader className="py-2 px-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <FolderKanban className="w-4 h-4 text-blue-600" />
                          <CardTitle className="text-sm text-slate-900">Projets ({pendingProjects.length})</CardTitle>
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => navigate('/projects')} className="h-7 text-xs">
                          <ArrowUpRight className="w-3 h-3" />
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent className="pt-0 px-4 pb-3">
                      <div className="space-y-1">
                        {pendingProjects.slice(0, 3).map((project) => (
                          <div 
                            key={project.id}
                            className="flex items-center justify-between py-1.5 hover:bg-blue-100/50 -mx-2 px-2 rounded cursor-pointer"
                            onClick={() => navigate(`/projects/${project.id}`)}
                          >
                            <span className="text-sm text-slate-700 truncate flex-1">{project.name}</span>
                            <Badge variant="outline" className="text-[10px] h-5 ml-2">
                              {project.status === 'draft' ? 'Brouillon' : 'À valider'}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Pending Tickets */}
                {pendingTickets.length > 0 && (
                  <Card className="border-orange-200 bg-orange-50/50">
                    <CardHeader className="py-2 px-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <MessageSquare className="w-4 h-4 text-orange-600" />
                          <CardTitle className="text-sm text-slate-900">Tickets ({pendingTickets.length})</CardTitle>
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => navigate('/tickets')} className="h-7 text-xs">
                          <ArrowUpRight className="w-3 h-3" />
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent className="pt-0 px-4 pb-3">
                      <div className="space-y-1">
                        {pendingTickets.slice(0, 3).map((ticket) => (
                          <div 
                            key={ticket.id}
                            className="flex items-center justify-between py-1.5 hover:bg-orange-100/50 -mx-2 px-2 rounded cursor-pointer"
                            onClick={() => navigate(`/tickets/${ticket.id}`)}
                          >
                            <div className="flex items-center gap-2 min-w-0 flex-1">
                              {(ticket.priority === 'high' || ticket.priority === 'urgent') && (
                                <AlertTriangle className="w-3 h-3 text-red-500 shrink-0" />
                              )}
                              <span className="text-sm text-slate-700 truncate">{ticket.title}</span>
                            </div>
                            <Badge variant="outline" className={cn(
                              "text-[10px] h-5 ml-2",
                              ticket.priority === 'urgent' && "bg-red-50 text-red-600",
                              ticket.priority === 'high' && "bg-orange-50 text-orange-600"
                            )}>
                              {ticket.priority === 'urgent' ? 'Urgent' : ticket.priority === 'high' ? 'Haut' : 'Normal'}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Tasks Awaiting Client */}
                {tasksAwaitingClient.length > 0 && (
                  <Card className="border-purple-200 bg-purple-50/50">
                    <CardHeader className="py-2 px-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4 text-purple-600" />
                          <CardTitle className="text-sm text-slate-900">Validation client ({tasksAwaitingClient.length})</CardTitle>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="pt-0 px-4 pb-3">
                      <div className="space-y-1">
                        {tasksAwaitingClient.slice(0, 3).map((task) => (
                          <div 
                            key={task.id}
                            className="flex items-center justify-between py-1.5 hover:bg-purple-100/50 -mx-2 px-2 rounded cursor-pointer"
                            onClick={() => navigate(`/projects/${task.project_id}`)}
                          >
                            <span className="text-sm text-slate-700 truncate flex-1">{task.name}</span>
                            <Badge variant="outline" className="text-[10px] h-5 ml-2 bg-purple-50 text-purple-600">
                              V{task.current_version || 1}/{task.max_versions || 3}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Pending Quotes */}
                {pendingQuotes.length > 0 && (
                  <Card className="border-emerald-200 bg-emerald-50/50">
                    <CardHeader className="py-2 px-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4 text-emerald-600" />
                          <CardTitle className="text-sm text-slate-900">Devis ({pendingQuotes.length})</CardTitle>
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => navigate('/quotes')} className="h-7 text-xs">
                          <ArrowUpRight className="w-3 h-3" />
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent className="pt-0 px-4 pb-3">
                      <div className="space-y-1">
                        {pendingQuotes.slice(0, 3).map((quote) => (
                          <div 
                            key={quote.id}
                            className="flex items-center justify-between py-1.5 hover:bg-emerald-100/50 -mx-2 px-2 rounded cursor-pointer"
                            onClick={() => navigate('/quotes')}
                          >
                            <span className="text-sm text-slate-700 truncate flex-1">
                              {quote.first_name} {quote.last_name}
                            </span>
                            <Badge variant="outline" className="text-[10px] h-5 ml-2 bg-emerald-50 text-emerald-600">
                              Nouveau
                            </Badge>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}

            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <Card className="card card-hover" data-testid="stat-revenue">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-[#64748b] mb-1">Chiffre d'affaires</p>
                      <p className="font-mono text-2xl font-bold text-[#1a365d]">
                        {(dashboard?.total_revenue || 0).toLocaleString('fr-FR')}€
                      </p>
                      <div className="flex items-center gap-1 mt-2 text-[#00d4aa]">
                        <TrendingUp size={14} />
                        <span className="text-xs font-medium">+12.5%</span>
                      </div>
                    </div>
                    <div className="w-12 h-12 bg-[#00d4aa]/10 rounded-xl flex items-center justify-center">
                      <DollarSign className="w-6 h-6 text-[#00d4aa]" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="card card-hover" data-testid="stat-margin">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-[#64748b] mb-1">Marge nette</p>
                      <p className="font-mono text-2xl font-bold text-[#1a365d]">
                        {(dashboard?.total_margin || 0).toLocaleString('fr-FR')}€
                      </p>
                      <div className="flex items-center gap-1 mt-2 text-[#00d4aa]">
                        <TrendingUp size={14} />
                        <span className="text-xs font-medium">+8.2%</span>
                      </div>
                    </div>
                    <div className="w-12 h-12 bg-[#1a365d]/10 rounded-xl flex items-center justify-center">
                      <TrendingUp className="w-6 h-6 text-[#1a365d]" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="card card-hover" data-testid="stat-projects">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-[#64748b] mb-1">Projets actifs</p>
                      <p className="font-mono text-2xl font-bold text-[#1a365d]">
                        {dashboard?.active_projects || 0}
                      </p>
                    </div>
                    <div className="w-12 h-12 bg-[#6366f1]/10 rounded-xl flex items-center justify-center">
                      <FolderKanban className="w-6 h-6 text-[#6366f1]" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="card card-hover" data-testid="stat-clients">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-[#64748b] mb-1">Clients</p>
                      <p className="font-mono text-2xl font-bold text-[#1a365d]">
                        {dashboard?.total_clients || 0}
                      </p>
                    </div>
                    <div className="w-12 h-12 bg-[#f59e0b]/10 rounded-xl flex items-center justify-center">
                      <Users className="w-6 h-6 text-[#f59e0b]" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Charts Row */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
              {/* Revenue Chart */}
              <Card className="card lg:col-span-2" data-testid="revenue-chart">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-lg font-semibold text-[#1a365d]">
                    Évolution du CA
                  </CardTitle>
                  <Button variant="ghost" size="icon">
                    <MoreHorizontal className="w-4 h-4" />
                  </Button>
                </CardHeader>
                <CardContent>
                  <div className="h-64 min-h-[256px]" style={{ minWidth: '300px' }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={dashboard?.monthly_revenue || []}>
                        <defs>
                          <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#00d4aa" stopOpacity={0.3}/>
                            <stop offset="95%" stopColor="#00d4aa" stopOpacity={0}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="month" stroke="#64748b" fontSize={12} />
                        <YAxis stroke="#64748b" fontSize={12} />
                        <Tooltip 
                          contentStyle={{ 
                            background: '#fff', 
                            border: '1px solid #e2e8f0',
                            borderRadius: '8px'
                          }}
                          formatter={(value) => [`${value.toLocaleString('fr-FR')}€`, 'CA']}
                        />
                        <Area 
                          type="monotone" 
                          dataKey="revenue" 
                          stroke="#00d4aa" 
                          strokeWidth={2}
                          fillOpacity={1} 
                          fill="url(#colorRevenue)" 
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              {/* Revenue by Type */}
              <Card className="card" data-testid="revenue-by-type">
                <CardHeader>
                  <CardTitle className="text-lg font-semibold text-[#1a365d]">
                    Répartition par type
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-48 min-h-[192px]" style={{ minWidth: '200px' }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={pieData}
                          cx="50%"
                          cy="50%"
                          innerRadius={50}
                          outerRadius={70}
                          paddingAngle={2}
                          dataKey="value"
                        >
                          {pieData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value) => `${value.toLocaleString('fr-FR')}€`} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-4 justify-center">
                    {pieData.map((entry, index) => (
                      <div key={entry.name} className="flex items-center gap-1.5">
                        <div 
                          className="w-2 h-2 rounded-full" 
                          style={{ backgroundColor: COLORS[index % COLORS.length] }}
                        />
                        <span className="text-xs text-[#64748b]">{entry.name}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Top Clients */}
            <Card className="card" data-testid="top-clients">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg font-semibold text-[#1a365d]">
                  Meilleurs clients
                </CardTitle>
                <Button variant="ghost" size="sm" className="text-[#00d4aa]">
                  Voir tout <ArrowUpRight className="w-4 h-4 ml-1" />
                </Button>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {(dashboard?.top_clients || []).map((client, index) => (
                    <div 
                      key={client.id} 
                      className="flex items-center justify-between p-3 rounded-lg hover:bg-[#f8fafc] transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-[#1a365d]/10 flex items-center justify-center">
                          <span className="font-semibold text-[#1a365d]">
                            {client.name?.split(' ').map(n => n[0]).join('').toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium text-[#1a365d]">{client.name}</p>
                          <p className="text-xs text-[#64748b]">#{index + 1} client</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-mono font-bold text-[#1a365d]">
                          {client.revenue?.toLocaleString('fr-FR')}€
                        </p>
                        <p className="text-xs text-[#64748b]">CA total</p>
                      </div>
                    </div>
                  ))}
                  {(!dashboard?.top_clients || dashboard.top_clients.length === 0) && (
                    <div className="text-center py-8 text-[#64748b]">
                      <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
                      <p>Aucun client pour le moment</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </main>
    </div>
  );
};

export default AgencyDashboard;
